// Theme management shared by the app. The active theme is expressed as a
// `data-theme` attribute on <html> ('light' | 'dark' | 'nature' |
// 'nature-dark'), which the CSS variables in index.css key off. The initial
// value is also set by an inline script in public/index.html before React
// mounts so there is no flash of the wrong theme; the logic here must stay in
// sync with that script.

const STORAGE_KEY = 'theme';

// The selectable themes. 'nature' is a soft blue/teal/peach palette matching the
// app logo, with 'nature-dark' its dark-surface variant; light and dark are the
// neutral defaults. Keep in sync with the CSS blocks in index.css and the
// no-flash script in public/index.html.
export const THEMES = ['light', 'dark', 'nature', 'nature-dark'];

function isValidTheme(value) {
  return THEMES.includes(value);
}

// Saved preference if the user has chosen one, otherwise the OS setting.
export function getInitialTheme() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (isValidTheme(saved)) {
      return saved;
    }
  } catch {
    // localStorage can be unavailable (private mode, blocked cookies); fall
    // through to the OS preference.
  }

  const prefersDark =
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-color-scheme: dark)').matches;
  return prefersDark ? 'dark' : 'light';
}

// Reflect the theme onto <html> so the CSS variables switch.
export function applyTheme(theme) {
  document.documentElement.dataset.theme = theme;
}

// Apply and persist the user's explicit choice.
export function setTheme(theme) {
  applyTheme(theme);
  try {
    localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    // Persisting is best-effort; the in-memory attribute still took effect.
  }
}
